import { useState, Dispatch, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import "./ShipmentDetails.scss";

import Alert from "components/v2/Alert/Alert";
import OrganisationForm from "pages/shipper/RequestForm/components/Organisation/OrganisationForm";
import { RequestFormAction, RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import ScheduleForm from "./components/Schedule/ScheduleForm";
import PackingList from "./components/PackingList/PackingList";
import ShipmentRoute from "./components/ShipmentRoute/ShipmentRoute";
import SelectedOrders from "./components/SelectedOrders/SelectedOrders";

interface IShipmentDetailsProps {
  data: any;
  isLoading: boolean;
  setCurrentStep: Function;
  state: RequestFormState;
  dispatch: Dispatch<RequestFormAction>;
  childRef: React.MutableRefObject<any>;
  refs: {
    organisationFormRef: React.RefObject<HTMLDivElement>;
    scheduleFormRef: React.RefObject<HTMLDivElement>;
  };
  removedOrders: string[];
  changedOrders: string[];
  setRemovedOrders: Function;
  setChangedOrders: Function;
}

function ShipmentDetails({
  data,
  state,
  dispatch,
  setCurrentStep,
  isLoading,
  refs,
  childRef,
  removedOrders,
  changedOrders,
  setRemovedOrders,
  setChangedOrders,
}: IShipmentDetailsProps) {
  const intl = useIntl();
  const methods = useFormContext();
  const { setValue } = methods;
  const [showAlert, setShowAlert] = useState<boolean>(true);
  const quickRequestData = data.map((QuickRequest: any) => QuickRequest.data);

  const onAlertClose = () => {
    setShowAlert(false);
    setRemovedOrders("");
    setChangedOrders("");
  };

  const commonPaymentLocation =
    quickRequestData.length &&
    quickRequestData.every(
      (item: any) =>
        item.organization.payment_location_reference === quickRequestData[0].organization.payment_location_reference,
    )
      ? quickRequestData[0].organization.payment_location_reference
      : null;

  useEffect(() => {
    setValue("organizational.location", commonPaymentLocation);
  }, [commonPaymentLocation, data]);

  // to displays alerts
  const renderAlert = () => {
    if (!showAlert) return null;

    if (removedOrders.length > 0) {
      return (
        <Alert
          variant="warning"
          onClose={onAlertClose}
          label={`${removedOrders.length} ${intl.formatMessage({
            id: "new-request.schedule.removedShipment",
            defaultMessage: "order has been removed from your shipment",
          })}`}
        >
          <FormattedMessage
            id="new-request.schedule.modifiedOrder"
            defaultMessage="The following order has been modified and is no longer valid for this shipment:"
          />
          <ul style={{ listStyleType: "disc" }}>
            {removedOrders.map((order: string, index: number) => (
              <li key={index} className="mt-2">
                &#x2022; <span className="text-blue font-medium">{order}</span>
              </li>
            ))}
          </ul>
        </Alert>
      );
    }
    if (changedOrders.length > 0) {
      return (
        <Alert
          variant="brand"
          onClose={onAlertClose}
          label={`${changedOrders.length} ${intl.formatMessage({
            id: "new-request.schedule.changedShipment",
            defaultMessage: "Changes were made to the packing list in an order.",
          })}`}
        >
          <FormattedMessage
            id="new-request.schedule.changedShipmentMessage"
            defaultMessage="Please check your shipment details again. Order that was modified:"
          />
          <ul style={{ listStyleType: "disc" }}>
            {changedOrders.map((order: string, index: number) => (
              <li key={index} className="mt-2">
                &#x2022; <span className="text-blue font-medium">{order}</span>
              </li>
            ))}
          </ul>
        </Alert>
      );
    }
  };

  return (
    <div>
      <div className="display-sm font-semibold">{renderAlert()}</div>
      <div className="display-sm font-semibold">
        <FormattedMessage id="new-request.organisational-details.shipmentDetails" defaultMessage="Shipment details" />
      </div>
      <ShipmentRoute isLoading={isLoading} />
      <SelectedOrders data={data} setCurrentStep={setCurrentStep} isLoading={isLoading} />
      <div ref={refs.organisationFormRef} className="mt-3xl">
        <OrganisationForm
          state={state}
          dispatch={dispatch}
          isQuoteRequest
          isMiniTender={false}
          isNewShipment={false}
          isLoadingShipmentBasic={false}
          isConsolidated
          childRef={childRef}
        />
      </div>
      <div ref={refs.scheduleFormRef}>
        <ScheduleForm data={data} />
      </div>
      <PackingList data={data} isLoading={isLoading} />
    </div>
  );
}

export default ShipmentDetails;
